/**
 * Google Analytics (GA4) event helpers.
 *
 * The gtag.js snippet is loaded in index.html with send_page_view disabled;
 * SPA page views and the signup/checkout funnel events are sent from here.
 * Attribution parameters (see utils/attribution.ts) are attached to
 * conversion events - register them as custom dimensions in GA4 to report
 * on them.
 */

import { getGaAttributionParams } from '../utils/attribution';

const PENDING_PURCHASE_KEY = 'ga_pending_purchase';
const PURCHASE_TRACKED_PREFIX = 'ga_purchase_tracked_';

export interface CheckoutItem {
  item_id?: string;
  item_name?: string;
  price?: number;
  quantity?: number;
}

export interface CheckoutDetails {
  value?: number;
  currency?: string;
  items?: CheckoutItem[];
}

const gtag = (...args: unknown[]): void => {
  const g = (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag;
  if (typeof g === 'function') {
    try {
      g(...args);
    } catch (error) {
      console.warn('GA: gtag call failed:', error);
    }
  }
};

const googleAnalytics = {
  trackPageView(path: string, title?: string): void {
    gtag('event', 'page_view', {
      page_path: path,
      page_location: window.location.href,
      page_title: title || document.title
    });
  },

  trackSignUp(method: string = 'email'): void {
    gtag('event', 'sign_up', { method, ...getGaAttributionParams() });
  },

  trackLogin(method: string = 'email'): void {
    gtag('event', 'login', { method });
  },

  /**
   * Fire begin_checkout and stash the checkout value so the purchase event
   * on the payment success page can report it (Stripe only returns a
   * session_id to the success URL, not the amount).
   */
  trackBeginCheckout(details: CheckoutDetails = {}): void {
    gtag('event', 'begin_checkout', {
      currency: details.currency || 'AUD',
      value: details.value,
      items: details.items,
      ...getGaAttributionParams()
    });
    try {
      sessionStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify(details));
    } catch {
      // Storage unavailable - purchase will be reported without a value
    }
  },

  /**
   * Deduplicated per transaction, so success-page refreshes don't double count.
   * Returns the checkout details it reported, or null if this transaction was already reported.
   */
  trackPurchase(transactionId: string, details?: CheckoutDetails): CheckoutDetails | null {
    try {
      if (sessionStorage.getItem(PURCHASE_TRACKED_PREFIX + transactionId)) {
        return null;
      }
    } catch {
      // Storage unavailable - fall through and report anyway
    }

    let resolved: CheckoutDetails = details || {};
    if (!details) {
      try {
        resolved = JSON.parse(sessionStorage.getItem(PENDING_PURCHASE_KEY) || '{}');
      } catch {
        resolved = {};
      }
    }

    gtag('event', 'purchase', {
      transaction_id: transactionId,
      currency: resolved.currency || 'AUD',
      value: resolved.value,
      items: resolved.items,
      ...getGaAttributionParams()
    });

    try {
      sessionStorage.setItem(PURCHASE_TRACKED_PREFIX + transactionId, '1');
      sessionStorage.removeItem(PENDING_PURCHASE_KEY);
    } catch {
      // Storage unavailable - nothing to clean up
    }
    return resolved;
  },

  trackEvent(name: string, params: Record<string, unknown> = {}): void {
    gtag('event', name, params);
  }
};

export default googleAnalytics;
